import React, { useEffect, useState } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import {
  Box,
  Button,
  Container,
  TextField,
  Typography,
  Divider,
  Stack,
  InputAdornment,
  Grid,
  CircularProgress,
  TextareaAutosize,
} from "@common-ui/ui";
import { useHistory } from "react-router-dom";
import { Email, Phone, Link, Business, Numbers } from "@mui/icons-material";

import { createCustomer } from "../../services";

import AlertDialog from "./AlertDialog";
import MandatoryIcon from "../../components/MandatoryIcon";
import { useSelector } from "react-redux";

export const CreateCustomerForm = () => {
  let router = useHistory();

  const [isLoading, setIsLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [customerSystemId, setCustomerSystemId] = useState(null);

  const currentOrgId = useSelector(
    (state) => state.organization?.currentOrganization?.id
  );

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function fetchData() {
    try {
      setIsLoading(false);
    } catch (error) {
      alert(error);
    }
  }

  const formik = useFormik({
    initialValues: {
      name: "",
      account: "",
      email: "",
      phone: "",
      website: "",
      notes: "",
    },
    validationSchema: Yup.object({
      name: Yup.string().trim().max(255).required("Client Name is required"),
      account: Yup.string().trim(),
      email: Yup.string().email().trim(),
      phone: Yup.number(),
      website: Yup.string().url().trim(),
      notes: Yup.string().trim(),
    }),
    onSubmit: () => {
      async function submit() {
        let { name, account, email, phone, website, notes } = formik.values;
        let attributes = {
          name,
          account,
          email,
          phone,
          website,
          notes,
        };
        try {
          const payload = await onSubmit(attributes, currentOrgId);
          setCustomerSystemId(payload?.id);
          setOpen(true);
        } catch (error) {
          console.log(error);
          alert(error);
          formik.setSubmitting(false);
        }
      }
      submit();
    },
  });

  const onSubmit = async (attributes, orgId) => {
    // eslint-disable-next-line no-async-promise-executor
    return new Promise(async (resolve, reject) => {
      try {
        const payload = await createCustomer(attributes, orgId);
        resolve(payload);
      } catch (error) {
        reject(error);
      }
    });
  };

  const handleClose = () => {
    router.push({
      pathname: `/customers/${customerSystemId}/${currentOrgId}`,
    });
    setOpen(false);
  };

  return isLoading ? (
    <Box
      component="main"
      sx={{
        alignItems: "center",
        display: "flex",
        flexGrow: 1,
        minHeight: "100%",
        ".MuiDataGrid-virtualScroller": {
          marginTop: "40px !important",
        },
        // bgcolor: "background.paper",
      }}
    >
      <Container maxWidth="md" component="main">
        <Box sx={{ my: 3 }}>
          <CircularProgress />
        </Box>
      </Container>
    </Box>
  ) : (
    <>
      <Box
        component="main"
        sx={{
          alignItems: "center",
          display: "flex",
          flexGrow: 1,
          minHeight: "100%",
          backgroundColor: "#FFFFFF",
          ".MuiDataGrid-virtualScroller": {
            marginTop: "40px !important",
          },
          ".MuiDataGrid-columnHeaderTitle": {
            fontWeight: 700,
          },
        }}
      >
        <Container maxWidth="md" component="main">
          <form onSubmit={formik.handleSubmit}>
            <Box sx={{ my: 3 }}></Box>

            <Stack direction="column" sx={{ maxWidth: { md: 600 } }}>
              <Grid container>
                <Grid item container spacing={1} xs={12} md={6}>
                  <Grid item md={12} xs={12}>
                    <Typography sx={{ mt: 2 }} color="textPrimary" variant="h4">
                      New Client
                    </Typography>
                  </Grid>
                  <Grid item md={12} xs={12}></Grid>
                  <Grid item md={12} xs={12}>
                    <div>
                      <Typography>
                        <MandatoryIcon />
                        Client Name
                      </Typography>
                      <TextField
                        sx={{ mt: 1 }}
                        size="small"
                        InputProps={{
                          endAdornment: (
                            <InputAdornment position="end">
                              <Business />
                            </InputAdornment>
                          ),
                        }}
                        autoComplete="off"
                        error={Boolean(
                          formik.touched.name && formik.errors.name
                        )}
                        fullWidth
                        helperText={formik.touched.name && formik.errors.name}
                        placeholder="Type"
                        margin="normal"
                        name="name"
                        onBlur={formik.handleBlur}
                        onChange={formik.handleChange}
                        value={formik.values.name}
                        variant="outlined"
                      />
                    </div>
                  </Grid>
                  <Grid item md={12} xs={12}>
                    {" "}
                    <div>
                      <Typography>Account Number</Typography>
                      <TextField
                        sx={{ mt: 1 }}
                        size="small"
                        InputProps={{
                          endAdornment: (
                            <InputAdornment position="end">
                              <Numbers />
                            </InputAdornment>
                          ),
                        }}
                        autoComplete="off"
                        error={Boolean(
                          formik.touched.account && formik.errors.account
                        )}
                        fullWidth
                        helperText={
                          formik.touched.account && formik.errors.account
                        }
                        placeholder="Type"
                        margin="normal"
                        name="account"
                        onBlur={formik.handleBlur}
                        onChange={formik.handleChange}
                        type="account"
                        value={formik.values.account}
                        variant="outlined"
                      />
                    </div>
                  </Grid>
                  <Grid item md={12} xs={12}>
                    {" "}
                    <div>
                      <Typography>Email</Typography>
                      <TextField
                        sx={{ mt: 1 }}
                        size="small"
                        InputProps={{
                          endAdornment: (
                            <InputAdornment position="end">
                              <Email />
                            </InputAdornment>
                          ),
                        }}
                        autoComplete="off"
                        error={Boolean(
                          formik.touched.email && formik.errors.email
                        )}
                        fullWidth
                        helperText={formik.touched.email && formik.errors.email}
                        placeholder="Type"
                        margin="normal"
                        name="email"
                        onBlur={formik.handleBlur}
                        onChange={formik.handleChange}
                        type="email"
                        value={formik.values.email}
                        variant="outlined"
                      />
                    </div>
                  </Grid>
                  <Grid item md={12} xs={12}>
                    <div>
                      <Typography>Phone</Typography>

                      <Stack direction="row">
                        <TextField
                          sx={{ mt: 1 }}
                          size="small"
                          InputProps={{
                            endAdornment: (
                              <InputAdornment position="end">
                                <Phone />
                              </InputAdornment>
                            ),
                          }}
                          autoComplete="off"
                          error={Boolean(
                            formik.touched.phone && formik.errors.phone
                          )}
                          fullWidth
                          helperText={
                            formik.touched.phone && formik.errors.phone
                          }
                          placeholder="Type"
                          margin="normal"
                          name="phone"
                          onBlur={formik.handleBlur}
                          onChange={formik.handleChange}
                          type="number"
                          value={formik.values.phone}
                          variant="outlined"
                        />
                      </Stack>
                    </div>
                  </Grid>
                  <Grid item md={12} xs={12}>
                    {" "}
                    <div>
                      <Typography>Website</Typography>
                      <TextField
                        sx={{ mt: 1 }}
                        size="small"
                        InputProps={{
                          endAdornment: (
                            <InputAdornment position="end">
                              <Link />
                            </InputAdornment>
                          ),
                        }}
                        autoComplete="off"
                        error={Boolean(
                          formik.touched.website && formik.errors.website
                        )}
                        fullWidth
                        helperText={
                          formik.touched.website && formik.errors.website
                        }
                        placeholder="Type"
                        margin="normal"
                        name="website"
                        onBlur={formik.handleBlur}
                        onChange={formik.handleChange}
                        type="website"
                        value={formik.values.website}
                        variant="outlined"
                      />
                    </div>
                  </Grid>
                  <Grid item md={12} xs={12}>
                    {" "}
                    <div>
                      <Typography>Notes / Comments</Typography>
                      <TextField
                        sx={{ mt: 1 }}
                        size="small"
                        error={Boolean(
                          formik.touched.notes && formik.errors.notes
                        )}
                        fullWidth
                        helperText={formik.touched.notes && formik.errors.notes}
                        placeholder="Type"
                        margin="normal"
                        name="notes"
                        onBlur={formik.handleBlur}
                        onChange={formik.handleChange}
                        InputProps={{
                          inputComponent: TextareaAutosize,
                          minRows: 4,
                          maxRows: 12,
                        }}
                        value={formik.values.notes}
                        variant="outlined"
                      />
                    </div>
                  </Grid>
                </Grid>
              </Grid>
            </Stack>

            <Stack sx={{ maxWidth: { md: 600 } }}>
              <Divider sx={{ mb: 1, mt: 2 }} />

              <Stack
                direction={{ xs: "column", md: "row" }}
                justifyContent="flex-end"
                spacing={{ xs: 1, md: 2 }}
                sx={{ py: 2 }}
              >
                <div>
                  <Button
                    color="primary"
                    // disabled={formik.isSubmitting}
                    fullWidth
                    size="large"
                    onClick={() => router.goBack()}
                    // type="submit"
                    variant="text"
                  >
                    Cancel
                  </Button>
                </div>
                <div>
                  <Button
                    color="primary"
                    disabled={!(formik.isValid && formik.dirty)}
                    fullWidth
                    size="large"
                    type="submit"
                    variant="contained"
                  >
                    Save
                  </Button>
                </div>
              </Stack>
            </Stack>
          </form>
        </Container>
      </Box>
      <AlertDialog
        handleClose={handleClose}
        open={open}
        name={formik.values.name}
      />
    </>
  );
};
